<template>
  <BasePopup ref="popup" title="Start reading today">
    <div class="popup__inner">
      <form class="subscription-form" @submit="submitForm">
        <StripeCard
          v-if="visible && billing.cardEditMode"
          ref="stripeCard"
          :billing="billing"
          :show-button="false"
          @saved="billing.cardEditMode = false"
        />
        <StripeCardInfo
          v-if="visible && !billing.cardEditMode"
          ref="stripeCardInfo"
          :billing="billing"
          @change="onChangeHandler"
        />
        <v-wait for="Subscribing">
          <template v-slot:waiting>
            <span class="subscription-form__btn btns btns--large">
              Processing...
              <button-spinner />
            </span>
          </template>
          <a class="subscription-form__btn btns btns--large" @click="subscribe">
            <span v-if="trialByPlan15535Treatment()">Subscribe Now</span>
            <span v-else-if="billing.isTrialOver">Subscribe</span>
            <span v-else>Start 5-day free trial</span>
          </a>
        </v-wait>
        <div class="pre-auth-message">
          Upon entering your card details, we will authorize and
          <b><u>immediately refund</u></b> a charge of $1. The trial will still
          cost $0.
        </div>
        <FormError :errors="validation.errors" :field="plan.id" />
        <button type="submit" style="display: none" />
      </form>
    </div>
    <div v-if="!billing.isTrialOver" class="popup__tips">
      <p class="popup__tips-text">
        By clicking the
        {{
          trialByPlan15535Treatment()
            ? '"Subscribe Now"'
            : '"Start 5-day free trial"'
        }}
        button above, you agree to our
        <router-link target="_blank" :to="{ name: 'site_terms' }"
          >Terms of Use</router-link
        >
        and
        <router-link target="_blank" :to="{ name: 'site_privacy' }"
          >Privacy Policy</router-link
        >.
        <span v-if="hasTrialDays"
          >You may cancel any time in your free trial and you will not be
          charged.
        </span>
        To cancel, go to Billing and click on "Cancel subscription."
        <br /><br />

        <strong v-if="hasTrialDays">
          Shortform will automatically continue your subscription at the end of
          your free trial on {{ trialEnd }} and
          {{ subscribePopupChargesText }}
        </strong>

        There are no refunds or credits for partial subscription periods.
      </p>
    </div>
    <div v-else-if="billing.isTrialOver" class="popup__tips">
      <p class="popup__tips-text">
        After you click the "Subscribe" button above,
        <strong>Shortform will {{ subscribePopupChargesText }}</strong>
        You may cancel your subscription at any time. To cancel, go to Billing
        and click on "Cancel subscription." There are no refunds or credits for
        partial subscription periods.
        <br /><br />
      </p>
    </div>
  </BasePopup>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useWait } from '@/app/composables/useWait'

import { BillingView, PlanView } from '@/models/billing'
import { ValidationState } from '@/helpers/form'

import FormError from '@/components/ui/FormError.vue'
import ButtonSpinner from '@/components/ui/ButtonSpinner.vue'
import StripeCard from '@/app/components/billing/StripeCard.vue'
import StripeCardInfo from '@/app/components/billing/StripeCardInfo.vue'
import BasePopup from '@/components/ui/BasePopup.vue'

import { trialByPlan15535Variant } from '@/services/ab'
import {
  getTrialPolicy,
  PlatformTrialPolicy,
  trialByPlan15535Treatment,
} from '@/services/ab/15535_trial_by_plan'

const props = defineProps<{
  billing: BillingView
  plan: PlanView
}>()

const wait = useWait()
const emit = defineEmits(['subscribed'])

const popup = ref<InstanceType<typeof BasePopup> | null>(null)
const stripeCard = ref<InstanceType<typeof StripeCard> | null>(null)

const visible = ref(false)
const validation = ref(new ValidationState())
const trialPolicy = ref<PlatformTrialPolicy | null>(null)

async function show(): Promise<void> {
  const variant = trialByPlan15535Variant()
  if (variant) {
    const period = props.plan.id === 'plan_monthly' ? 'monthly' : 'annual'
    trialPolicy.value = await getTrialPolicy(variant, period)
  }
  visible.value = true
  popup.value?.show()
}

function hide(): void {
  visible.value = false
  validation.value.reset()
  popup.value?.hide()
}

function onChangeHandler(): void {
  props.billing.cardEditMode = true
  validation.value.reset()
}

async function submitForm(event: Event): Promise<void> {
  event.preventDefault()
  await subscribe()
}

const hasTrialDays = computed(() => {
  if (!trialPolicy.value) {
    return true
  }
  return trialPolicy.value.trial_days !== null
})

const trialEnd = computed(() => {
  return props.billing.trialEnd(props.plan.id)
})

async function subscribe(): Promise<void> {
  try {
    let cardSuccess = true
    wait.start('Subscribing')
    if (props.billing.cardEditMode && stripeCard.value?.needsSaving()) {
      cardSuccess = await stripeCard.value?.save()
    }
    if (cardSuccess) {
      await props.billing.subscribeTo(props.plan)
      emit('subscribed')
      validation.value.reset()
      hide()
    }
    wait.end('Subscribing')
  } catch (error) {
    wait.end('Subscribing')
    validation.value.showErrors(error)
  }
}

const subscribePopupChargesText = computed(() => {
  if (props.plan.nickname === 'Annual' && props.plan.discount) {
    return (
      `charge the subscription fee of ${props.plan.price.perPeriod} to your payment ` +
      `method for your first ${props.plan.labels.period}, ` +
      `then ${props.plan.priceNoDiscount.perPeriod} ` +
      `for subsequent ${props.plan.labels.period}s on ${props.plan.labels.basis} basis until you cancel.`
    )
  }

  return (
    `charge the subscription fee of ${props.plan.price.perPeriod} ` +
    `to your payment method on ${props.plan.labels.basis} basis until you cancel.`
  )
})

defineExpose({
  show,
  hide,
})
</script>

<style lang="scss" scoped>
.pre-auth-message {
  width: 100%;
  background-color: $c-yellow-8;
  border-radius: 10px;
  padding: 16px 20px;
  font-size: 11px;
  color: $c-black-3;
  line-height: 1.5;
  margin: 16px auto;
}
</style>
